import { mountLivingWindows } from './bjs-home-372e92a3d657-mansion-living-windows.js';
import { mountRoomProof } from './bjs-home-372e92a3d657-mansion-room-proof.js';
import { mountMosaicMotion } from './bjs-home-372e92a3d657-scripts-mosaic-motion.js';
import { mountLightbox } from './bjs-home-372e92a3d657-scripts-lightbox.js';

const mounts = new WeakMap();
export function mountHomepage(root) {
  if (!root || mounts.has(root)) return mounts.get(root);
  const doc = root.ownerDocument, win = doc.defaultView;
  const mansion = root.querySelector('[data-bjs-living-windows]');
  const stage = mansion.querySelector('.house-stage');
  const mosaic = root.querySelector('[data-culinary-mosaic]');
  const controller = mountLivingWindows(mansion);
  const rooms = mountRoomProof(mansion);
  const tiles = mountMosaicMotion(mosaic);
  const reduced = win.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedData = win.matchMedia('(prefers-reduced-data: reduce)');
  const connection = win.navigator.connection;
  const controls = [...root.querySelectorAll('[data-page-motion]')];
  const status = root.querySelector('[data-motion-status]');
  const listeners = [];
  const on = (el, event, fn) => {
    el?.addEventListener?.(event, fn);
    listeners.push(() => el?.removeEventListener?.(event, fn));
  };
  const state = { enabled: true, heroVisible: false, mosaicVisible: false, modal: false,
    hover: false, focus: false, pressed: false, pageVisible: !doc.hidden, disposed: false };

  function sync() {
    if (state.disposed) return;
    const blocked = reduced.matches || reducedData.matches || connection?.saveData === true;
    const interacting = state.hover || state.focus || state.pressed || state.modal;
    const allowed = state.enabled && !blocked && state.pageVisible && !interacting;
    // One page-level budget: mansion has priority at the overlap of the two regions.
    controller?.setSuspended(!allowed || !state.heroVisible);
    controller?.setEnabled(state.enabled);
    tiles.setRunning(allowed && !state.heroVisible && state.mosaicVisible);
    for (const button of controls) {
      button.disabled = blocked;
      button.setAttribute('aria-pressed', String(state.enabled));
      button.textContent = blocked ? 'Motion off' : state.enabled ? 'Pause page motion' : 'Enable page motion';
    }
    status.textContent = reduced.matches ? 'Still imagery follows your reduced-motion preference.' :
      (reducedData.matches || connection?.saveData === true) ? 'Still imagery follows your data-saver preference.' :
      state.enabled ? 'Page motion enabled. It pauses while you explore photographs or leave the page.' :
      'Page motion is off. You can enable it at any time.';
  }
  for (const button of controls) on(button, 'click', () => { state.enabled = !state.enabled; sync(); });
  on(mansion, 'bjs:motion-preference', event => { state.enabled = event.detail.enabled === true; sync(); });
  on(mosaic, 'pointerover', event => {
    if (event.pointerType !== 'touch' && event.target.closest('[data-photo-tile]')) { state.hover = true; sync(); }
  });
  on(mosaic, 'pointerout', event => {
    if (!event.relatedTarget?.closest?.('[data-photo-tile]')) { state.hover = false; sync(); }
  });
  on(mosaic, 'pointerleave', () => { state.hover = false; sync(); });
  on(mosaic, 'focusin', () => { state.focus = true; sync(); });
  on(mosaic, 'focusout', event => { state.focus = mosaic.contains(event.relatedTarget); sync(); });
  on(mosaic, 'pointerdown', () => { state.pressed = true; sync(); });
  const release = () => { state.pressed = false; sync(); };
  on(win, 'pointerup', release); on(win, 'pointercancel', release);
  on(doc, 'visibilitychange', () => { state.pageVisible = !doc.hidden; if (doc.hidden) state.pressed = false; sync(); });
  on(win, 'pagehide', () => { state.pageVisible = false; sync(); });
  on(win, 'pageshow', () => { state.pageVisible = !doc.hidden; sync(); });
  on(reduced, 'change', sync); on(reducedData, 'change', sync); on(connection, 'change', sync);
  const lightbox = mountLightbox(root, open => { state.modal = open; sync(); });
  let observer = null;
  if (win.IntersectionObserver) {
    observer = new win.IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.target === stage) state.heroVisible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
        if (entry.target === mosaic) state.mosaicVisible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
      }
      sync();
    }, { threshold: [0, 0.1] });
    observer.observe(stage); observer.observe(mosaic);
  }
  const api = { dispose() {
    if (state.disposed) return;
    state.disposed = true;
    observer?.disconnect(); listeners.forEach(off => off());
    lightbox.dispose(); tiles.dispose(); rooms?.dispose(); controller?.dispose();
    mounts.delete(root);
  } };
  mounts.set(root, api); sync(); return api;
}
function init(scope = document) {
  if (scope.matches?.('[data-bjs-homepage]')) mountHomepage(scope);
  scope.querySelectorAll?.('[data-bjs-homepage]').forEach(mountHomepage);
}
if (typeof document !== 'undefined') {
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-bjs-homepage]')) mounts.get(event.target)?.dispose();
    event.target.querySelectorAll?.('[data-bjs-homepage]').forEach(root => mounts.get(root)?.dispose());
  });
}
